import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Steps,
  Table,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useActiveCustomers } from '@/hooks/useCustomers';
import { useCurrency } from '@/contexts/CurrencyContext';
import {
  quotationConsolidationService,
  type ConsolidationLineRow,
  type ConsolidationPreviewResult,
  type EligibleQuotationRow,
} from '@/services/quotationConsolidation.service';

const { Title, Text } = Typography;

type LineAward = { quotation_item_id: string; award_qty: number };

function apiErrorMessage(e: unknown, fallback: string): string {
  const err = e as {
    message?: string;
    response?: { data?: { message?: string; error?: string } };
  };
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.message ||
    fallback
  );
}

const QuotationConsolidationPage: React.FC = () => {
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const { data: customers = [], isLoading: customersLoading } = useActiveCustomers();

  const [step, setStep] = useState(0);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [quotations, setQuotations] = useState<EligibleQuotationRow[]>([]);
  const [selectedQuotationIds, setSelectedQuotationIds] = useState<string[]>([]);
  const [lines, setLines] = useState<ConsolidationLineRow[]>([]);
  const [awards, setAwards] = useState<Record<string, number>>({});
  const [preview, setPreview] = useState<ConsolidationPreviewResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [headerForm] = Form.useForm();

  const loadQuotations = useCallback(async (cid: string) => {
    setLoading(true);
    try {
      const rows = await quotationConsolidationService.listEligibleQuotations(cid);
      setQuotations(rows);
      setSelectedQuotationIds([]);
      setLines([]);
      setAwards({});
      setPreview(null);
    } catch (e: unknown) {
      message.error(apiErrorMessage(e, 'Could not load quotations'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLines = useCallback(async (qIds: string[]) => {
    setLoading(true);
    try {
      const rows = await quotationConsolidationService.loadLines(qIds);
      setLines(rows);
      const initial: Record<string, number> = {};
      for (const row of rows) {
        if (row.remaining_qty > 0) {
          initial[row.quotation_item_id] = row.remaining_qty;
        }
      }
      setAwards(initial);
    } catch (e: unknown) {
      message.error(apiErrorMessage(e, 'Could not load lines'));
    } finally {
      setLoading(false);
    }
  }, []);

  const buildPayload = useCallback(() => {
    const linePayload: LineAward[] = Object.entries(awards)
      .filter(([, qty]) => Number(qty) > 0)
      .map(([quotation_item_id, award_qty]) => ({
        quotation_item_id,
        award_qty: Number(award_qty),
      }));
    const header = headerForm.getFieldsValue();
    return {
      customer_id: customerId!,
      quotation_ids: selectedQuotationIds,
      lines: linePayload,
      customer_po_number: header.customer_po_number,
      customer_po_date: header.customer_po_date
        ? dayjs(header.customer_po_date).format('YYYY-MM-DD')
        : undefined,
      delivery_terms: header.delivery_terms,
      payment_terms: header.payment_terms,
      remarks: header.remarks,
    };
  }, [awards, customerId, headerForm, selectedQuotationIds]);

  const quotationColumns: ColumnsType<EligibleQuotationRow> = [
    { title: 'Quotation', dataIndex: 'quotation_number', key: 'no' },
    { title: 'Rev', dataIndex: 'revision_number', key: 'rev', width: 50 },
    {
      title: 'Date',
      dataIndex: 'quotation_date',
      key: 'date',
      render: (v) => (v ? dayjs(v).format('DD-MMM-YYYY') : '—'),
    },
    {
      title: 'Expiry',
      dataIndex: 'valid_until',
      key: 'exp',
      render: (v) => (v ? dayjs(v).format('DD-MMM-YYYY') : '—'),
    },
    { title: 'Project', dataIndex: 'project_name', key: 'proj', ellipsis: true },
    {
      title: 'Value',
      dataIndex: 'grand_total',
      key: 'val',
      align: 'right',
      render: (v) => formatCurrency(v),
    },
    { title: 'Status', dataIndex: 'status', key: 'st', width: 130 },
    {
      title: 'Converted',
      dataIndex: 'converted_value',
      key: 'cv',
      align: 'right',
      render: (v) => formatCurrency(v),
    },
    {
      title: 'Remaining',
      dataIndex: 'remaining_value',
      key: 'rv',
      align: 'right',
      render: (v) => formatCurrency(v),
    },
  ];

  const lineColumns: ColumnsType<ConsolidationLineRow> = [
    { title: 'QT', dataIndex: 'quotation_number', key: 'q', width: 110 },
    { title: 'Line', dataIndex: 'line_number', key: 'ln', width: 50 },
    { title: 'Code', dataIndex: 'item_code', key: 'code', width: 90 },
    { title: 'Description', dataIndex: 'item_description', key: 'desc', ellipsis: true },
    {
      title: 'Original',
      dataIndex: 'original_qty',
      key: 'oq',
      width: 90,
      align: 'right',
    },
    {
      title: 'Converted',
      dataIndex: 'converted_qty',
      key: 'cq',
      width: 90,
      align: 'right',
    },
    {
      title: 'Remaining',
      dataIndex: 'remaining_qty',
      key: 'rq',
      width: 90,
      align: 'right',
    },
    {
      title: 'Award qty',
      key: 'award',
      width: 120,
      render: (_, row) => (
        <InputNumber
          min={0}
          max={row.remaining_qty}
          step={0.0001}
          value={awards[row.quotation_item_id] ?? 0}
          disabled={row.remaining_qty <= 0}
          onChange={(v) =>
            setAwards((prev) => ({
              ...prev,
              [row.quotation_item_id]: Number(v ?? 0),
            }))
          }
          style={{ width: '100%' }}
        />
      ),
    },
  ];

  const customerOptions = useMemo(
    () =>
      (customers as Array<{ id: string; customer_name: string }>).map((c) => ({
        value: c.id,
        label: c.customer_name,
      })),
    [customers]
  );

  const nextFromCustomer = () => {
    if (!customerId) {
      message.warning('Select a customer');
      return;
    }
    loadQuotations(customerId);
    setStep(1);
  };

  const nextFromQuotations = () => {
    if (!selectedQuotationIds.length) {
      message.warning('Select at least one quotation');
      return;
    }
    loadLines(selectedQuotationIds);
    setStep(2);
  };

  const nextFromLines = async () => {
    const hasAward = Object.values(awards).some((q) => Number(q) > 0);
    if (!hasAward) {
      message.warning('Enter award quantity on at least one line');
      return;
    }
    setLoading(true);
    try {
      const result = await quotationConsolidationService.preview(buildPayload());
      setPreview(result);
      setStep(3);
    } catch (e: unknown) {
      message.error(apiErrorMessage(e, 'Preview failed'));
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      const order = await quotationConsolidationService.create(buildPayload());
      message.success(`Customer order ${order.sales_order_number} created`);
      navigate(`/growth/orders/${order.id}`);
    } catch (e: unknown) {
      message.error(apiErrorMessage(e, 'Could not create customer order'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Title level={3}>Create Customer Order from Quotations</Title>
      <Text type="secondary">
        Consolidate multiple accepted quotations for one customer into a single customer order.
      </Text>

      <Steps
        current={step}
        style={{ margin: '24px 0' }}
        items={[
          { title: 'Customer' },
          { title: 'Quotations' },
          { title: 'Lines' },
          { title: 'Preview' },
          { title: 'Create SO' },
        ]}
      />

      {step === 0 && (
        <Card title="Select customer">
          <Select
            showSearch
            placeholder="Customer"
            style={{ width: '100%', maxWidth: 480 }}
            loading={customersLoading}
            options={customerOptions}
            optionFilterProp="label"
            value={customerId ?? undefined}
            onChange={(v) => setCustomerId(v)}
          />
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={nextFromCustomer}>
              Next
            </Button>
          </div>
        </Card>
      )}

      {step === 1 && (
        <Card title="Select quotations">
          <Table
            rowKey="id"
            loading={loading}
            columns={quotationColumns}
            dataSource={quotations}
            pagination={false}
            rowSelection={{
              selectedRowKeys: selectedQuotationIds,
              onChange: (keys) => setSelectedQuotationIds(keys as string[]),
            }}
            size="small"
          />
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setStep(0)}>Back</Button>
            <Button type="primary" onClick={nextFromQuotations}>
              Next
            </Button>
          </Space>
        </Card>
      )}

      {step === 2 && (
        <Card title="Select lines and award quantities">
          <Table
            rowKey="quotation_item_id"
            loading={loading}
            columns={lineColumns}
            dataSource={lines}
            pagination={false}
            size="small"
            scroll={{ x: 900 }}
          />
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setStep(1)}>Back</Button>
            <Button type="primary" onClick={nextFromLines}>
              Preview
            </Button>
          </Space>
        </Card>
      )}

      {step === 3 && preview && (
        <Card title="Consolidation preview">
          {preview.warnings.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Warnings"
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {preview.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              }
            />
          )}
          <Text strong>Total value: {formatCurrency(preview.total_value)}</Text>
          <Table
            style={{ marginTop: 12 }}
            size="small"
            rowKey="quotation_item_id"
            pagination={false}
            dataSource={preview.selected_lines}
            columns={[
              { title: 'Quotation', dataIndex: 'quotation_number' },
              { title: 'Item', dataIndex: 'item_code' },
              { title: 'Description', dataIndex: 'item_description', ellipsis: true },
              { title: 'Award qty', dataIndex: 'award_qty', align: 'right' },
              {
                title: 'Value',
                dataIndex: 'line_value',
                align: 'right',
                render: (v) => formatCurrency(v),
              },
            ]}
          />
          <Space style={{ marginTop: 16 }}>
            <Button onClick={() => setStep(2)}>Back</Button>
            <Button type="primary" onClick={() => setStep(4)}>
              Continue
            </Button>
          </Space>
        </Card>
      )}

      {step === 4 && (
        <Card title="Customer order details">
          <Form form={headerForm} layout="vertical" style={{ maxWidth: 520 }}>
            <Form.Item name="customer_po_number" label="Customer PO number">
              <Input />
            </Form.Item>
            <Form.Item name="customer_po_date" label="Customer PO date">
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="delivery_terms" label="Delivery terms">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item name="payment_terms" label="Payment terms">
              <Input.TextArea rows={2} />
            </Form.Item>
            <Form.Item name="remarks" label="Remarks">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Form>
          <Space>
            <Button onClick={() => setStep(3)}>Back</Button>
            <Button type="primary" loading={loading} onClick={submit}>
              Create customer order
            </Button>
          </Space>
        </Card>
      )}
    </div>
  );
};

export default QuotationConsolidationPage;
